// Libraries
import { ListItem } from '@chakra-ui/react';
import { useMemo } from 'react';
// Types
import { VoyageListItemProps } from './VoyageListItem.type';

/**
 * Renders a list item for a voyage in the VoyageList component.
 *
 * @component
 * @param {VoyageListItemProps} props - The props for the VoyageListItem component.
 * @param {ReactNode} props.children - The content of the list item.
 * @param {Function} props.handleClick - The click event handler for the list item.
 * @param {boolean} props.isSelected - Indicates whether the list item is currently selected.
 * @returns {ReactElement} The rendered VoyageListItem component.
 */
export const VoyageListItem = (props: VoyageListItemProps) => {
  const { children, handleClick, isSelected } = props;

  const borderHoverWhenSelected = useMemo(() => (isSelected ? 'baseInsetBorder' : 'blueNavyInsetBorder'), [isSelected]);
  const borderWhenSelected = useMemo(() => (isSelected ? 'baseInsetBorder' : 'none'), [isSelected]);
  const filterWhenNotSelected = useMemo(
    () =>
      !isSelected &&
      'drop-shadow(0px 4px 5.32px rgba(35, 35, 45, 0.24)) drop-shadow(0px 2.76726px 2.21381px rgba(195, 194, 209, 0.03))',
    [isSelected]
  );

  return (
    <ListItem
      borderWidth='1px'
      borderStyle='none'
      borderBottomStyle='solid'
      borderColor='border.100'
      transition='all 0.2s ease-in-out'
      cursor='pointer'
      _last={{ border: 'none' }}
      _hover={{
        boxShadow: borderHoverWhenSelected,
        filter: filterWhenNotSelected,
      }}
      boxShadow={borderWhenSelected}
      onClick={handleClick}
    >
      {children}
    </ListItem>
  );
};
